import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import FarmersNav from "@/components/FarmersNav";
import {
  ArrowLeft, ArrowRight, Check, Storefront, Palette, MapPin, Plant,
  Sparkle, House, CheckCircle, MagicWand,
} from "@phosphor-icons/react";
import { fpPath } from "@/lib/site";

/**
 * FarmersWebsiteWizard — /farmers/websites/new
 *
 * 4-step onboarding to launch a farm website in minutes:
 *   1. Farm identity (name, tagline, farm types, short description)
 *   2. Location (country, state, city + address)
 *   3. Starter package + theme
 *   4. Confirm & launch → creates + seeds pages, redirects to editor
 */
export default function FarmersWebsiteWizard() {
  const nav = useNavigate();
  const { user, requireSignIn } = useAuth();
  const [step, setStep] = useState(1);
  const [themes, setThemes] = useState([]);
  const [starters, setStarters] = useState([]);
  const [farmTypes, setFarmTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    farm_name: "",
    tagline: "",
    short_description: "",
    farm_types: [],
    country: "",
    state: "",
    city: "",
    village: "",
    postal_code: "",
    address_lines: [""],
    show_exact_location: true,
    theme_id: "farm-market",
    starter_package: "farm-seller",
    slug: "",
  });

  useEffect(() => {
    http.get("/farmers/websites/themes").then(({ data }) => {
      setThemes(data.themes || []);
      setStarters(data.starters || []);
    }).catch(() => toast.error("Couldn't load theme catalog."));
    http.get("/farmers/websites/farm-types").then(({ data }) => {
      setFarmTypes(data.farm_types || []);
    }).catch(() => {});
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleType = (t) => setForm((f) => ({
    ...f,
    farm_types: f.farm_types.includes(t) ? f.farm_types.filter((x) => x !== t) : [...f.farm_types, t].slice(0, 6),
  }));

  const slugPreview = useMemo(() => {
    const raw = form.slug || form.farm_name || "";
    return raw.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "my-farm";
  }, [form.slug, form.farm_name]);

  const canNext = useMemo(() => {
    if (step === 1) return form.farm_name.trim().length >= 2;
    if (step === 2) return form.country.trim().length >= 2;
    if (step === 3) return form.theme_id && form.starter_package;
    return true;
  }, [step, form]);

  const submit = async () => {
    if (!user) return requireSignIn();
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.address_lines = form.address_lines.filter((l) => l && l.trim());
      const { data } = await http.post("/farmers/websites", payload);
      toast.success("Your farm site is ready!");
      nav(fpPath(`/websites/${data.website.id}`));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create your site — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 px-5 md:px-10">
        <FarmersNav />
        <div className="max-w-2xl mx-auto text-center py-20">
          <Storefront size={40} weight="duotone" className="mx-auto text-[#B85F2A]" />
          <h1 className="mt-4 font-serif-display text-4xl">Sign in to launch your farm site</h1>
          <p className="mt-2 text-[#3A3830]">Your farm site is tied to your Farmers Pulse account.</p>
          <button onClick={requireSignIn}
            className="mt-6 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 text-sm"
            data-testid="wizard-signin-cta">Sign in to continue</button>
        </div>
      </main>
    );
  }

  const totalSteps = 4;

  return (
    <main className="min-h-screen bg-[#F5EBDC] pt-32 md:pt-36 pb-20 px-5 md:px-10" data-testid="farmers-website-wizard">
      <FarmersNav />
      <div className="max-w-4xl mx-auto">
        <Link to={fpPath("/mine")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> My Farm
        </Link>

        <div className="mt-3 flex items-center justify-between">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <Sparkle size={12} weight="fill" className="inline mr-1" /> Create My Farm Website
            </div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
              Launch your farm site in minutes.
            </h1>
          </div>
          <div className="hidden md:block text-right">
            <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">Step</div>
            <div className="text-2xl font-serif">{step} / {totalSteps}</div>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-6 flex items-center gap-2">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex-1 h-1.5 rounded-full"
              style={{ background: n <= step ? "#B85F2A" : "rgba(0,0,0,0.08)" }} />
          ))}
        </div>

        {step === 1 && (
          <Link to={fpPath("/websites/import")}
            className="mt-6 flex items-center justify-between gap-3 rounded-2xl border border-black/10 bg-gradient-to-br from-[#FFF3E7] to-white p-4 hover:border-[#B85F2A] transition group"
            data-testid="wizard-import-shortcut">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#151312] text-[#F5EBDC] flex items-center justify-center shrink-0">
                <MagicWand size={16} weight="fill" />
              </div>
              <div>
                <div className="text-sm font-medium">Already have a farm website?</div>
                <div className="text-xs text-[#3A3830]">Paste your current URL — our AI clones every page.</div>
              </div>
            </div>
            <ArrowRight size={14} weight="bold" className="text-[#B85F2A] group-hover:translate-x-1 transition" />
          </Link>
        )}

        <section className="mt-8 rounded-3xl border border-black/10 bg-white p-6 md:p-10" data-testid={`wizard-step-${step}`}>
          {step === 1 && (
            <Step1 form={form} setField={setField} toggleType={toggleType} farmTypes={farmTypes} slug={slugPreview} />
          )}
          {step === 2 && <Step2 form={form} setField={setField} />}
          {step === 3 && (
            <Step3 form={form} setField={setField} themes={themes} starters={starters} />
          )}
          {step === 4 && <Step4 form={form} slug={slugPreview} themes={themes} starters={starters} />}

          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="rounded-full border border-black/15 px-5 py-2.5 text-sm disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="wizard-back">
              <ArrowLeft size={12} weight="bold" /> Back
            </button>
            {step < totalSteps ? (
              <button onClick={() => canNext && setStep((s) => s + 1)}
                disabled={!canNext}
                className="rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-40"
                data-testid="wizard-next">
                Continue <ArrowRight size={12} weight="bold" />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting}
                className="rounded-full bg-[#B85F2A] text-white px-5 py-2.5 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="wizard-submit">
                {submitting ? "Creating…" : "Launch my site"} <CheckCircle size={13} weight="fill" />
              </button>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

function Step1({ form, setField, toggleType, farmTypes, slug }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#B85F2A]">Step 1 · Farm identity</div>
        <h2 className="mt-1 font-serif-display text-3xl">Tell us about your farm</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Farm name" required>
          <input value={form.farm_name} onChange={(e) => setField("farm_name", e.target.value)}
            placeholder="Sunny Meadow Farm"
            className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
            data-testid="wizard-farm-name" />
        </Field>
        <Field label="Web address" hint="your farm site's URL — you can edit this later">
          <div className="flex items-center gap-1 rounded-lg border border-black/15 px-3 py-2 text-sm bg-neutral-50">
            <span className="text-[#3A3830]">/f/</span>
            <input value={form.slug} onChange={(e) => setField("slug", e.target.value)}
              placeholder={slug} className="flex-1 bg-transparent outline-none"
              data-testid="wizard-slug" />
          </div>
        </Field>
      </div>
      <Field label="Tagline" hint="one line that describes your farm">
        <input value={form.tagline} onChange={(e) => setField("tagline", e.target.value)}
          placeholder="Farm-fresh vegetables, herbs & honey"
          className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
          data-testid="wizard-tagline" />
      </Field>
      <Field label="Short description" hint="max 300 chars — appears on the hero + share previews">
        <textarea value={form.short_description} onChange={(e) => setField("short_description", e.target.value)}
          maxLength={300} rows={3}
          placeholder="A small family farm growing seasonal vegetables and heirloom tomatoes just outside town."
          className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
          data-testid="wizard-short-desc" />
      </Field>
      <Field label="What best describes your farm?" hint={`pick up to 6 · ${form.farm_types.length}/6`}>
        <div className="flex flex-wrap gap-1.5">
          {farmTypes.map((t) => {
            const on = form.farm_types.includes(t);
            return (
              <button key={t} type="button" onClick={() => toggleType(t)}
                className={`text-xs rounded-full border px-2.5 py-1.5 capitalize ${on ? "bg-[#B85F2A] text-white border-[#B85F2A]" : "bg-white text-[#151312] border-black/15 hover:border-[#B85F2A]"}`}
                data-testid={`wizard-farm-type-${t}`}>
                {t.replace(/-/g, " ")}
              </button>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

function Step2({ form, setField }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#B85F2A]">Step 2 · Location</div>
        <h2 className="mt-1 font-serif-display text-3xl">Where is your farm?</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Country" required>
          <input value={form.country} onChange={(e) => setField("country", e.target.value)}
            placeholder="India" className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
            data-testid="wizard-country" />
        </Field>
        <Field label="State / Province">
          <input value={form.state} onChange={(e) => setField("state", e.target.value)}
            placeholder="Kerala" className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
            data-testid="wizard-state" />
        </Field>
        <Field label="City / Town">
          <input value={form.city} onChange={(e) => setField("city", e.target.value)}
            placeholder="Kochi" className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
            data-testid="wizard-city" />
        </Field>
        <Field label="Village / Neighborhood">
          <input value={form.village} onChange={(e) => setField("village", e.target.value)}
            placeholder="" className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
            data-testid="wizard-village" />
        </Field>
        <Field label="Postal code">
          <input value={form.postal_code} onChange={(e) => setField("postal_code", e.target.value)}
            placeholder="" className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
            data-testid="wizard-postal" />
        </Field>
        <Field label="Address line" hint="street address / plot number">
          <input value={form.address_lines[0] || ""}
            onChange={(e) => setField("address_lines", [e.target.value])}
            className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
            data-testid="wizard-address" />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.show_exact_location}
          onChange={(e) => setField("show_exact_location", e.target.checked)}
          data-testid="wizard-show-exact-location" />
        Show exact location on my public site
      </label>
    </div>
  );
}

function Step3({ form, setField, themes, starters }) {
  return (
    <div className="space-y-8">
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#B85F2A]">Step 3 · Look &amp; pages</div>
        <h2 className="mt-1 font-serif-display text-3xl">Pick a starter pack and a theme</h2>
        <p className="mt-1 text-sm text-[#3A3830]">Themes are presentation only — you can switch anytime without losing content.</p>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] mb-2">Starter pack</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {starters.map((s) => {
            const on = form.starter_package === s.id;
            return (
              <button key={s.id} type="button" onClick={() => setField("starter_package", s.id)}
                className={`text-left rounded-2xl border p-4 transition ${on ? "border-[#B85F2A] bg-[#FFF3E7]" : "border-black/10 bg-white hover:border-[#B85F2A]"}`}
                data-testid={`wizard-starter-${s.id}`}>
                <div className="text-sm font-medium">{s.label}</div>
                <div className="mt-1 text-xs text-[#3A3830]">{s.description}</div>
                {on && <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.22em] text-[#B85F2A]"><Check size={10} weight="bold" /> Chosen</div>}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] mb-2 flex items-center gap-1">
          <Palette size={11} weight="fill" /> Theme
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {themes.map((t) => {
            const on = form.theme_id === t.id;
            const live = t.status === "live";
            return (
              <button key={t.id} type="button"
                onClick={() => live && setField("theme_id", t.id)}
                disabled={!live}
                className={`text-left rounded-2xl border overflow-hidden transition ${on ? "border-[#B85F2A] ring-2 ring-[#B85F2A]/40" : "border-black/10 hover:border-[#B85F2A]"} ${!live ? "opacity-70 cursor-not-allowed" : "bg-white"}`}
                data-testid={`wizard-theme-${t.id}`}>
                <div className="aspect-[16/10] bg-neutral-100 relative overflow-hidden">
                  {t.preview ? (
                    <img src={t.preview} alt={t.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-300">
                      <Plant size={30} weight="fill" />
                    </div>
                  )}
                  {!live && (
                    <span className="absolute top-2 right-2 rounded-full bg-white/90 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] font-mono">
                      Coming soon
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="mt-0.5 text-xs text-[#3A3830]">{t.eyebrow}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Step4({ form, slug, themes, starters }) {
  const theme = themes.find((t) => t.id === form.theme_id);
  const starter = starters.find((s) => s.id === form.starter_package);
  return (
    <div className="space-y-5">
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#B85F2A]">Step 4 · Confirm</div>
        <h2 className="mt-1 font-serif-display text-3xl">Ready to launch?</h2>
        <p className="mt-1 text-sm text-[#3A3830]">
          We'll create a draft site with the starter pack you chose. You can edit blocks, upload photos and publish from the site editor.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <SummaryCard title="Farm" items={[
          ["Name", form.farm_name],
          ["Tagline", form.tagline || "—"],
          ["Types", form.farm_types.join(", ") || "—"],
          ["Public URL", `/f/${slug}`],
        ]} />
        <SummaryCard title="Location" items={[
          ["Country", form.country],
          ["State", form.state || "—"],
          ["City", form.city || "—"],
          ["Show exact", form.show_exact_location ? "Yes" : "No"],
        ]} />
        <SummaryCard title="Starter pack" items={[
          ["Layout", starter?.label || form.starter_package],
          ["Includes", starter?.description || ""],
        ]} />
        <SummaryCard title="Theme" items={[
          ["Style", theme?.name || form.theme_id],
          ["Family", theme?.family || "—"],
        ]} />
      </div>
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900">
        <div className="font-medium">Your site starts as a draft.</div>
        You can preview it right away, edit every block, add products &amp; photos, and press <em>Publish</em> when you're ready to share.
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830] flex items-center gap-1">
        {label} {required && <span className="text-[#B85F2A]">*</span>}
      </div>
      {hint && <div className="text-xs text-[#3A3830] mt-0.5">{hint}</div>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function SummaryCard({ title, items }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-4">
      <div className="text-[10px] uppercase tracking-[0.28em] font-mono text-[#3A3830]">{title}</div>
      <dl className="mt-2 space-y-1 text-sm">
        {items.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-3">
            <dt className="text-[#3A3830]">{k}</dt>
            <dd className="text-right max-w-[60%] break-words">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
